// frontend/src/pages/AccountPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import SellerDetailsForm from '../components/invoice/SellerDetailsForm';
import { Seller, LegalAgreements } from '../types/invoiceTypes';
import logger from '../utils/logger';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { redirectToCustomerPortal } from '../api/stripeApi';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { IconContract, IconDownload, IconExternalLink, IconMail, IconNotifications, IconNotificationsOff, IconTheme } from '../icons';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ApiAccessSection from '../components/account/ApiAccessSection';
import ThemePreferenceSection from '../components/account/ThemePreferenceSection';
import InboundActivation from '../components/inbound/InboundActivation';
import { formatDate as formatDateCentral } from '../utils/formatters';

// URLs für rechtliche Dokumente
const LEGAL_DOCUMENT_URLS = {
  avv: 'https://firebasestorage.googleapis.com/v0/b/billution-invoices.firebasestorage.app/o/legal-documents%2FVereinbarung%20zur%20Auftragsverarbeitung%20v1.0.pdf?alt=media',
  agb: '/agb', // Interne Seite - wird später auf PDF umgestellt
  datenschutz: '/datenschutz', // Interne Seite - wird später auf PDF umgestellt
};

// Default empty seller data structure (copied from InvoiceDashboard)
const defaultSellerData: Seller = {
    name: '',
    id: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
    vatNumber: null,
    taxNumber: null,
    street2: null,
    globalId: null,
    globalIdScheme: null,
    // NEU: Handelsregister-Felder
    commercialRegisterNumber: null,
    commercialRegisterLocation: null,
    contact: {
        name: null,
        department: null,
        phone: null,
        fax: null,
        email: null,
        website: null,
    },
    communication: [],
    bankDetails: {
        iban: '',
        bic: '',
        bankName: ''
    },
    logoUrl: null,
    invoiceNumberFormat: null,
    invoiceNumberCurrent: null,
    invoiceNumberStart: null,
    // Felder für Angebotsnummern hinzufügen
    offerNumberFormat: null,
    offerNumberCurrent: null,
    offerNumberStart: null,
};

const AccountPage: React.FC = () => {
  const { t, i18n } = useTranslation('account');
  const { isMobile } = useDeviceDetection();
  
  // Hole die Nutzerdaten und Speicherfunktionen aus dem Auth-Kontext
  const { currentUser, sellerData, isPremium, sellerDataLoading, saveSellerData, refreshUserData } = useAuth();

  // Ref für den Scroll zum Formular
  const sellerFormRef = useRef<HTMLDivElement>(null);

  // States for managing the SellerDetailsForm (copied from InvoiceDashboard)
  const [localSellerData, setLocalSellerData] = useState<Seller>(defaultSellerData);
  const [isSellerDataChanged, setIsSellerDataChanged] = useState<boolean>(false);
  const [sellerSaveError, setSellerSaveError] = useState<string | null>(null);
  const [sellerSaveSuccess, setSellerSaveSuccess] = useState<boolean>(false);
  
  // Neue State-Variable, um fehlende Verkäuferdaten zu erkennen
  const [hasRequiredData, setHasRequiredData] = useState<boolean>(true);

  // Effect to initialize local seller data when loaded from context (copied from InvoiceDashboard)
  useEffect(() => {
      if (!sellerDataLoading && sellerData) {
          setLocalSellerData(sellerData);
          setIsSellerDataChanged(false); // Reset changed state after loading
          
          // Prüfe, ob wichtige Daten fehlen (konsistent mit neuer Validierung)
          const requiredFields = [
            sellerData.name?.trim(),
            sellerData.street?.trim(), 
            sellerData.zip?.trim(),
            sellerData.city?.trim(),
            sellerData.country?.trim()
          ];
          const missingData = requiredFields.some(field => !field || field === '');
          setHasRequiredData(!missingData);
          
          // Wenn Daten fehlen, scrolle zum Formular — außer ein Deeplink-Hash
          // ist gesetzt (z.B. #empfangspostfach): der Deeplink hat Vorrang vor
          // dem Auto-Scroll, sonst überschreibt dieser den gezielten Sprung zu
          // einem anderen Kartenbereich der Seite (Review-MINOR 🟡-2).
          if (missingData && sellerFormRef.current && typeof window !== 'undefined' && !window.location.hash) {
            sellerFormRef.current.scrollIntoView({ behavior: 'smooth' });
          }
      } else if (!sellerDataLoading && !sellerData && currentUser) {
          // If no data in Firestore, use defaults (ensure user exists)
          setLocalSellerData(defaultSellerData);
          setIsSellerDataChanged(false);
          setHasRequiredData(false);

          // Scrolle zum Formular — außer ein Deeplink-Hash ist gesetzt (siehe oben).
          setTimeout(() => {
            if (sellerFormRef.current && typeof window !== 'undefined' && !window.location.hash) {
              sellerFormRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 500);
      }
  }, [sellerData, sellerDataLoading, currentUser]);

  // Additional effect to sync logoUrl changes from context to local state
  // This ensures the logo preview updates when logoUrl changes in sellerData
  useEffect(() => {
      if (!sellerDataLoading && sellerData && localSellerData) {
          // Check if logoUrl has changed in the context but not in local state
          if (sellerData.logoUrl !== localSellerData.logoUrl) {
              setLocalSellerData(prevState => ({
                  ...prevState,
                  logoUrl: sellerData.logoUrl
              }));
          }
      }
  }, [sellerData?.logoUrl, sellerDataLoading, localSellerData?.logoUrl]);

  // Handle changes in the SellerDetailsForm
  const handleSellerChange = async (updatedSeller: Seller) => {
      setLocalSellerData(updatedSeller);
      
      // Check if only logoUrl has changed (logo upload/removal)
      const isLogoOnlyChange = updatedSeller.logoUrl !== localSellerData.logoUrl &&
                              Object.keys(updatedSeller).every(key => 
                                  key === 'logoUrl' || updatedSeller[key as keyof Seller] === localSellerData[key as keyof Seller]
                              );
      
      // If it's a logo change, save immediately to Firestore
      if (isLogoOnlyChange) {
          try {
              logger.info("Logo changed, saving to Firestore immediately");
              await saveSellerData({ logoUrl: updatedSeller.logoUrl });
              await refreshUserData(); // Refresh to ensure context is updated
              setSellerSaveSuccess(true);
              setTimeout(() => setSellerSaveSuccess(false), 3000);
          } catch (error: any) {
              logger.error(error, "Error saving logo to Firestore");
              setSellerSaveError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.upload.failed' }));
          }
      } else {
          // For other changes, just mark as changed
          setIsSellerDataChanged(true);
          setSellerSaveSuccess(false);
          setSellerSaveError(null);
      }
  };

  // Save changes to Firestore (copied from InvoiceDashboard)
  const handleSaveChanges = async () => {
      if (!isSellerDataChanged || !currentUser) return;
      
      // Prüfe, ob alle Pflichtfelder ausgefüllt sind (konsistent mit SellerDetailsForm)
      const requiredFields = [
        localSellerData.name?.trim(),
        localSellerData.street?.trim(), 
        localSellerData.zip?.trim(),
        localSellerData.city?.trim(),
        localSellerData.country?.trim()
      ];
      
      if (requiredFields.some(field => !field || field === '')) {
        setSellerSaveError(t('messages.requiredFields'));
        return;
      }
      
      setSellerSaveError(null);
      setSellerSaveSuccess(false);
      try {
          await saveSellerData(localSellerData); // Use context save function
          await refreshUserData(); // Daten neu laden, um sicherzustellen, dass alle Felder aktuell sind
          
          // After refreshing, use the updated data from context
          // This ensures we have the latest data including any server-side updates
          if (sellerData) {
              setLocalSellerData(sellerData);
          }
          
          setIsSellerDataChanged(false);
          setSellerSaveSuccess(true);
          setHasRequiredData(true); // Nach dem Speichern sind die Daten vollständig
          setTimeout(() => setSellerSaveSuccess(false), 3000); // Hide success message after 3s
      } catch (error: any) {
          logger.error(error, "error saving seller data");
          setSellerSaveError(mapErrorToUserMessage(error, t, { fallbackKey: 'messages.saveError' }));
      }
  };

  // Reset changes to the last saved state (from context) (copied from InvoiceDashboard)
  const handleResetChanges = () => {
      setLocalSellerData(sellerData || defaultSellerData);
      setIsSellerDataChanged(false);
      setSellerSaveError(null);
      setSellerSaveSuccess(false);
  };

  // Function to handle Firestore update after logo removal (copied from InvoiceDashboard)
  const handleLogoRemovedInFirestore = async () => {
      setSellerSaveError(null);
      setSellerSaveSuccess(false);
      try {
          // Explicitly save only the null value for logoUrl
          await saveSellerData({ logoUrl: null });
          
          // Update local state immediately to reflect the change
          setLocalSellerData(prevState => ({
              ...prevState,
              logoUrl: null
          }));
          
          await refreshUserData(); // Ensure context has the latest data
          logger.info("logoUrl successfully set to null in firestore");
          // We can optionally show a temporary success message here
      } catch (error: any) {
          logger.error(error, "error setting logoUrl to null in firestore");
          setSellerSaveError(mapErrorToUserMessage(error, t, { fallbackKey: 'messages.logoRemoveError' }));
      }
  };

  // Hilfsfunktion zum sicheren Formatieren eines Firestore-Timestamps (zentrales formatDate, M-G)
  const formatTimestamp = (timestamp: any): string => {
    if (timestamp && typeof timestamp.seconds === 'number') {
      return formatDateCentral(timestamp, { locale: i18n.language });
    }
    return 'N/A';
  };

  return (
    <div className="container mx-auto p-4 bg-surface min-h-screen text-primary">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold text-accent-ink">{t('title')}</h1>
        <Link
          to="/dashboard/pdf-design"
          className="flex items-center gap-2 px-4 py-2 bg-surface-inset text-primary rounded-lg hover:bg-surface-hover transition-colors border border-strong"
        >
          <IconTheme size={14} />
          PDF-Design anpassen
        </Link>
      </div>

      {/* Hinweis für Benutzer ohne vollständige Daten */}
      {!hasRequiredData && !sellerDataLoading && (
        <div className="bg-[var(--tone-accent-bg)] border border-indigo-500 p-4 mb-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-primary mb-2">{t('welcome.title')}</h2>
          <p className="text-secondary mb-4">
            {t('welcome.description')}
          </p>
          <ul className="list-disc list-inside text-secondary mb-2">
            <li>{t('welcome.requiredFields.company')}</li>
            <li>{t('welcome.requiredFields.street')}</li>
            <li>{t('welcome.requiredFields.postal')}</li>
            <li>{t('welcome.requiredFields.country')}</li>
          </ul>
          <p className="text-secondary text-sm italic">
            {t('welcome.afterSave')}
          </p>
        </div>
      )}

      {sellerDataLoading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">{t('loading.accountInfo')}</p>
        </div>
      ) : currentUser && !sellerData ? ( // Fall: User geladen, aber keine sellerData (z.B. neuer User vor erstem Speichern)
        <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 mb-6 text-primary">
          <h2 className="text-xl font-semibold mb-4 text-primary">{t('subscription.title')}</h2>
          <p className="mb-4 text-secondary">
            {t('subscription.completeProfile')}
          </p>
          {/* Optional: Direktlink zum Profildaten-Formular, falls es weiter oben ist und nicht automatisch gescrollt wird */}
          {/* <button onClick={() => sellerFormRef.current?.scrollIntoView({ behavior: 'smooth' })} className="text-accent-ink hover:underline">Profildaten jetzt vervollständigen</button> */}
          <p className="mt-4 text-secondary">Noch kein Premium-Abonnement?</p>
          <Link 
            to="/dashboard/abonnement" 
            className="mt-2 px-4 py-3 font-medium shadow-lg hover:shadow-xl bg-indigo-600 text-[#0C1322] rounded-md hover:bg-indigo-700 inline-block transition"
          >
            {t('subscription.becomePremium')}
          </Link>
        </div>
      ) : sellerData && isPremium ? (
        <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 mb-6 text-primary">
          <h2 className="text-xl font-semibold mb-4 text-primary">{t('subscription.title')}</h2>
          <p className="mb-2">
            <span className="font-semibold">{t('subscription.status')}:</span>{' '}
            <span className="text-accent-ink capitalize">{sellerData.subscriptionStatus || t('subscription.active')}</span>
          </p>
          <p className="mb-2">
            <span className="font-semibold">{t('subscription.currentPlan')}:</span> {sellerData.planName || t('subscription.premium')}
          </p>
          {sellerData.currentPeriodEnd && (
            <p className="mb-2">
              <span className="font-semibold">{t('subscription.renewsOn')}:</span>{' '}
              {formatTimestamp(sellerData.currentPeriodEnd)}
            </p>
          )}
          {sellerData.cancelAtPeriodEnd === true && (
            <p className="mt-4 text-[var(--tone-warning-fg)] font-semibold">
              {t('subscription.cancelled')}
            </p>
          )}
           {/* Button zum Stripe Portal nur wenn stripeCustomerId existiert */}
           {sellerData.stripeCustomerId && (
            <button
              onClick={async () => {
                // Logik aus MainLayout.tsx übernehmen und anpassen
                try {
                  const portalUrl = await redirectToCustomerPortal(); // Annahme: redirectToCustomerPortal ist hier verfügbar oder wird importiert
                  window.location.assign(portalUrl);
                } catch (error: any) {
                  logger.error(error, "portal-redirect-error");
                  // Fehler dem Nutzer anzeigen, z.B. mit einem State
                  alert(`${t('messages.portalError')}: ${error.message}`);
                }
              }}
              className="mt-6 px-4 py-3 font-medium shadow-lg hover:shadow-xl bg-surface-inset text-primary rounded-md hover:bg-surface-hover inline-block transition"
            >
              {t('subscription.managePortal')}
            </button>
          )}
        </div>
      ) : sellerData && !isPremium ? ( // Fall: sellerData existiert, aber kein Premium
        <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 mb-6 text-primary">
          <h2 className="text-xl font-semibold mb-4 text-primary">{t('subscription.title')}</h2>
          <p className="mb-4 text-secondary">{t('subscription.noPremium')}</p>
          <Link 
            to="/dashboard/abonnement" 
            className="px-4 py-3 font-medium shadow-lg hover:shadow-xl bg-indigo-600 text-[#0C1322] rounded-md hover:bg-indigo-700 inline-block transition"
          >
            {t('subscription.becomePremium')}
          </Link>
        </div>
      ) : ( // Fallback, sollte idealerweise nicht erreicht werden, wenn Logik oben vollständig ist
        <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 mb-6 text-primary">
          <p className="text-[var(--tone-danger-fg)]">{t('messages.statusError')}</p>
        </div>
      )}

      <div className="mt-6">
        <p className="mb-4 text-sm text-muted">
          {t('footer.stripeReturn')}
        </p>

        <Link 
          to="/dashboard" 
          className="text-accent-ink hover:text-primary transition"
        >
          {t('actions.backToDashboard')}
        </Link>
      </div>

      {/* Seller Details Form Section - mit Referenz für Auto-Scrolling */}
      {!sellerDataLoading && currentUser && (
        <div className="mt-8" ref={sellerFormRef}>
             <SellerDetailsForm
                seller={localSellerData} 
                setSeller={handleSellerChange} 
                onLogoRemoved={handleLogoRemovedInFirestore}
                compact={isMobile}
             />
             {/* Display save/reset buttons and messages */} 
             {sellerSaveError && (
                <div className="mt-4 p-3 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] rounded">
                    {t('common:error')}: {sellerSaveError}
                </div>
             )}
             {sellerSaveSuccess && (
                  <div className="mt-4 p-3 bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] text-[var(--tone-success-fg)] rounded">
                     {t('messages.saveSuccess')}
                 </div>
             )}
             {isSellerDataChanged && (
                <div className="mt-6 flex justify-end space-x-4 border-t border-hairline pt-6">
                    <button
                        onClick={handleResetChanges}
                        className={`px-4 py-3 rounded-md text-sm font-medium transition bg-transparent border border-indigo-500 hover:border-indigo-400 text-accent-ink hover:bg-[var(--tone-accent-bg)]`}
                    >
                        {t('actions.discardChanges')}
                    </button>
                    <button
                        onClick={handleSaveChanges}
                        className={`px-4 py-3 rounded-md text-sm font-medium transition text-[#0C1322] bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl`}
                    >
                        {t('actions.saveChanges')}
                    </button>
                </div>
            )}
         </div>
      )}

      <div className="mt-8">
        <p className="mb-4 text-sm text-muted">
          {t('footer.manageSubscription')}
        </p>
      </div>

      {/* Rechtliche Dokumente Section */}
      {!sellerDataLoading && currentUser && (
        <div className="mt-8 bg-surface-raised shadow-lg rounded-xl border border-hairline p-6">
          <h2 className="text-xl font-semibold mb-4 text-primary flex items-center">
            <IconContract className="mr-2 text-accent-ink" size={20} />
            {t('legalDocuments.title')}
          </h2>
          <p className="text-muted text-sm mb-4">
            {t('legalDocuments.description')}
          </p>

          {/* Zeige akzeptierte Versionen wenn vorhanden */}
          {sellerData?.legalAgreements && (
            <div className="mb-4 p-3 bg-[rgb(var(--surface-inset-rgb)/0.5)] rounded-lg border border-strong">
              <p className="text-xs text-muted mb-2">{t('legalDocuments.acceptedVersions')}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                {sellerData.legalAgreements.avv && (
                  <span className="text-secondary">
                    AVV: v{sellerData.legalAgreements.avv.documentVersion} 
                    <span className="text-faint ml-1">
                      ({formatDateCentral(sellerData.legalAgreements.avv.acceptedAt, { locale: i18n.language })})
                    </span>
                  </span>
                )}
                {sellerData.legalAgreements.agb && (
                  <span className="text-secondary">
                    AGB: v{sellerData.legalAgreements.agb.documentVersion}
                    <span className="text-faint ml-1">
                      ({formatDateCentral(sellerData.legalAgreements.agb.acceptedAt, { locale: i18n.language })})
                    </span>
                  </span>
                )}
                {sellerData.legalAgreements.datenschutz && (
                  <span className="text-secondary">
                    Datenschutz: v{sellerData.legalAgreements.datenschutz.documentVersion}
                    <span className="text-faint ml-1">
                      ({formatDateCentral(sellerData.legalAgreements.datenschutz.acceptedAt, { locale: i18n.language })})
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {/* Auftragsverarbeitungsvereinbarung (AVV) */}
            <div className="flex items-center justify-between p-3 bg-[rgb(var(--surface-inset-rgb)/0.3)] rounded-lg hover:bg-[rgb(var(--surface-inset-rgb)/0.5)] transition-colors">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-indigo-600/20 rounded-lg flex items-center justify-center mr-3">
                  <IconContract className="text-accent-ink" />
                </div>
                <div>
                  <p className="text-primary font-medium">{t('legalDocuments.avv.title')}</p>
                  <p className="text-muted text-xs">{t('legalDocuments.avv.version')}</p>
                </div>
              </div>
              <a
                href={LEGAL_DOCUMENT_URLS.avv}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] text-sm rounded-md transition-colors"
              >
                <IconDownload className="mr-2" size={14} />
                {t('legalDocuments.download')}
              </a>
            </div>

            {/* Allgemeine Geschäftsbedingungen (AGB) */}
            <div className="flex items-center justify-between p-3 bg-[rgb(var(--surface-inset-rgb)/0.3)] rounded-lg hover:bg-[rgb(var(--surface-inset-rgb)/0.5)] transition-colors">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-600/40 rounded-lg flex items-center justify-center mr-3">
                  <IconContract className="text-muted" />
                </div>
                <div>
                  <p className="text-primary font-medium">{t('legalDocuments.agb.title')}</p>
                  <p className="text-muted text-xs">{t('legalDocuments.agb.version')}</p>
                </div>
              </div>
              <Link
                to={LEGAL_DOCUMENT_URLS.agb}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2 bg-surface-hover hover:bg-gray-500 text-primary text-sm rounded-md transition-colors"
              >
                <IconExternalLink className="mr-2" size={14} />
                {t('legalDocuments.view')}
              </Link>
            </div>

            {/* Datenschutzerklärung */}
            <div className="flex items-center justify-between p-3 bg-[rgb(var(--surface-inset-rgb)/0.3)] rounded-lg hover:bg-[rgb(var(--surface-inset-rgb)/0.5)] transition-colors">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-600/40 rounded-lg flex items-center justify-center mr-3">
                  <IconContract className="text-muted" />
                </div>
                <div>
                  <p className="text-primary font-medium">{t('legalDocuments.datenschutz.title')}</p>
                  <p className="text-muted text-xs">{t('legalDocuments.datenschutz.version')}</p>
                </div>
              </div>
              <Link
                to={LEGAL_DOCUMENT_URLS.datenschutz}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2 bg-surface-hover hover:bg-gray-500 text-primary text-sm rounded-md transition-colors"
              >
                <IconExternalLink className="mr-2" size={14} />
                {t('legalDocuments.view')}
              </Link>
            </div>
          </div>

          <p className="mt-4 text-xs text-faint">
            {t('legalDocuments.pdfNotice')}
          </p>
        </div>
      )}

      {/* E-Mail-Einstellungen Section */}
      {!sellerDataLoading && currentUser && (
        <EmailPreferencesSection
          sellerData={sellerData}
          userId={currentUser.uid}
          onUpdate={refreshUserData}
        />
      )}

      {/* Darstellung (M-D5 W3-1) — self-gated, rendert null solange
          REACT_APP_UX_THEME_V5_ENABLED ≠ 'true' (inert). */}
      <ThemePreferenceSection />

      {/* API-Zugang Section — nur sichtbar wenn sellerData.apiAccess === true */}
      {!sellerDataLoading && sellerData?.apiAccess === true && (
        <ApiAccessSection sellerData={sellerData} />
      )}

      {/* Empfangspostfach (Loop A, WL-A4) — self-gated: rendert null solange
          REACT_APP_INBOUND_ENABLED ≠ 'true' (inert). */}
      {!sellerDataLoading && currentUser && (
        <InboundActivation sellerData={sellerData} />
      )}
    </div>
  );
};

// Separate Komponente für E-Mail-Einstellungen
interface EmailPreferencesSectionProps {
  sellerData: Seller | null;
  userId: string;
  onUpdate: () => void;
}

const EmailPreferencesSection: React.FC<EmailPreferencesSectionProps> = ({ 
  sellerData, 
  userId,
  onUpdate 
}) => {
  const { t } = useTranslation('account');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  const currentConsent = sellerData?.marketingConsent || 'functions';
  
  const handleConsentChange = async (newConsent: 'functions' | 'full' | 'none') => {
    if (newConsent === currentConsent) return;
    
    setIsUpdating(true);
    try {
      const sellerRef = doc(db, 'sellers', userId);
      const updateData: any = {
        marketingConsent: newConsent,
        marketingConsentUpdatedAt: new Date().toISOString(),
      };
      
      // Bei "full" → DOI noch nicht bestätigt
      if (newConsent === 'full') {
        updateData.marketingDoiConfirmed = false;
      }
      
      // Banner wieder anzeigen wenn von "none" gewechselt wird
      if (currentConsent === 'none' && newConsent !== 'none') {
        updateData.marketingBannerDismissed = false;
      }
      
      await updateDoc(sellerRef, updateData);
      
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
      onUpdate();
    } catch (error) {
      console.error('Error updating marketing consent:', error);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const consentOptions = [
    {
      value: 'full' as const,
      label: t('emailSettings.options.full.label'),
      description: t('emailSettings.options.full.description'),
      icon: <IconNotifications className="text-[var(--tone-success-fg)]" />,
      badge: sellerData?.marketingDoiConfirmed ? t('emailSettings.badges.confirmed') : t('emailSettings.badges.pendingConfirmation'),
      badgeColor: sellerData?.marketingDoiConfirmed ? 'bg-green-600' : 'bg-yellow-600',
    },
    {
      value: 'functions' as const,
      label: t('emailSettings.options.functions.label'),
      description: t('emailSettings.options.functions.description'),
      icon: <IconMail className="text-[var(--tone-info-fg)]" />,
      badge: t('emailSettings.badges.standard'),
      badgeColor: 'bg-blue-600',
    },
    {
      value: 'none' as const,
      label: t('emailSettings.options.none.label'),
      description: t('emailSettings.options.none.description'),
      icon: <IconNotificationsOff className="text-muted" />,
      badge: null,
      badgeColor: '',
    },
  ];
  
  return (
    <div className="mt-8 bg-surface-raised shadow-lg rounded-xl border border-hairline p-6">
      <h2 className="text-xl font-semibold mb-4 text-primary flex items-center">
        <IconMail className="mr-2 text-accent-ink" size={20} />
        {t('emailSettings.title')}
      </h2>
      <p className="text-muted text-sm mb-6">
        {t('emailSettings.description')}
      </p>
      
      {updateSuccess && (
        <div className="mb-4 p-3 bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] text-[var(--tone-success-fg)] rounded-lg text-sm">
          ✓ {t('emailSettings.saved')}
        </div>
      )}
      
      <div className="space-y-3">
        {consentOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleConsentChange(option.value)}
            disabled={isUpdating}
            className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
              currentConsent === option.value
                ? 'border-indigo-500 bg-[var(--tone-accent-bg)]'
                : 'border-hairline bg-[rgb(var(--surface-inset-rgb)/0.3)] hover:border-strong hover:bg-[rgb(var(--surface-inset-rgb)/0.5)]'
            } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-surface-raised">
                {option.icon}
              </div>
              <div className="text-left">
                <p className="text-primary font-medium">{option.label}</p>
                <p className="text-muted text-sm">{option.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {option.badge && currentConsent === option.value && (
                <span className={`px-2 py-1 text-xs rounded-full text-primary ${option.badgeColor}`}>
                  {option.badge}
                </span>
              )}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                currentConsent === option.value
                  ? 'border-indigo-500 bg-indigo-500'
                  : 'border-gray-500'
              }`}>
                {currentConsent === option.value && (
                  <svg className="w-3 h-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
      
      {currentConsent === 'full' && !sellerData?.marketingDoiConfirmed && (
        <div className="mt-4 p-3 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] text-[var(--tone-warning-fg)] rounded-lg text-sm">
          <strong>{t('emailSettings.badges.pendingConfirmation')}:</strong> {t('emailSettings.doiNotice')}
        </div>
      )}
      
      <p className="mt-4 text-xs text-faint">
        {t('emailSettings.changeNotice')}
      </p>
    </div>
  );
};

export default AccountPage;